import { MessageCircle, Send, X } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/cn';

const WHATSAPP_NUMBER = '233242366623';

const QUESTION_KEYS = ['demo', 'pricing', 'setup'] as const;

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.884 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function buildWhatsAppUrl(message: string) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

export const LandingWhatsAppWidget = () => {
  const { t } = useTranslation('landing');
  const panelTitleId = useId();
  const [open, setOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [teaserVisible, setTeaserVisible] = useState(true);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const dismissTeaser = useCallback(() => {
    setTeaserVisible(false);
  }, []);

  const openWhatsApp = useCallback((message: string) => {
    window.open(buildWhatsAppUrl(message), '_blank', 'noopener,noreferrer');
    setOpen(false);
    setCustomMessage('');
    dismissTeaser();
  }, [dismissTeaser]);

  const toggleOpen = () => {
    setOpen((current) => !current);
    dismissTeaser();
  };

  const handleCustomSend = () => {
    const trimmed = customMessage.trim();
    if (!trimmed) return;
    openWhatsApp(trimmed);
  };

  return (
    <div
      className="pointer-events-none fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-50 flex flex-col items-end gap-3"
      data-testid="landing-whatsapp-widget"
    >
      {teaserVisible && !open ? (
        <div className="pointer-events-auto landing-whatsapp-teaser relative max-w-[16.5rem] rounded-2xl border border-border/60 bg-background px-4 py-3.5 pr-10 shadow-e3">
          <button
            type="button"
            className="absolute right-2.5 top-2.5 flex size-8 items-center justify-center rounded-full text-fg-secondary transition-colors hover:bg-surface-sunken hover:text-fg"
            aria-label={t('whatsapp.dismissTeaser')}
            onClick={dismissTeaser}
          >
            <X className="size-4" aria-hidden />
          </button>
          <p className="text-body-sm font-semibold text-fg">{t('whatsapp.teaserTitle')}</p>
          <p className="mt-0.5 text-caption text-fg-secondary">{t('whatsapp.teaserBody')}</p>
        </div>
      ) : null}

      {open ? (
        <div
          role="dialog"
          aria-labelledby={panelTitleId}
          className="pointer-events-auto landing-whatsapp-panel w-[min(100vw-2rem,22rem)] overflow-hidden rounded-2xl border border-border/60 bg-background shadow-e3"
        >
          <div className="relative bg-[#128C7E] px-4 pb-4 pt-4 text-white">
            <button
              type="button"
              className="absolute right-3 top-3 flex size-9 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white"
              aria-label={t('whatsapp.close')}
              onClick={() => setOpen(false)}
            >
              <X className="size-4" aria-hidden />
            </button>
            <div className="flex items-start gap-3 pr-8">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/15">
                <WhatsAppIcon className="size-5" />
              </div>
              <div>
                <h2 id={panelTitleId} className="text-body font-semibold">
                  {t('whatsapp.panelTitle')}
                </h2>
                <p className="mt-0.5 text-caption text-white/85">{t('whatsapp.panelSubtitle')}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 px-4 py-4">
            <div>
              <p className="text-caption font-medium text-fg-secondary">{t('whatsapp.questionsIntro')}</p>
              <ul className="mt-2.5 space-y-2">
                {QUESTION_KEYS.map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      className="flex min-h-11 w-full items-center gap-2.5 rounded-xl border border-border bg-surface px-3.5 py-2.5 text-left text-body-sm font-medium text-fg transition-colors hover:border-[#25D366]/40 hover:bg-[#25D366]/5"
                      onClick={() => openWhatsApp(t(`whatsapp.messages.${key}`))}
                    >
                      <MessageCircle className="size-4 shrink-0 text-[#25D366]" aria-hidden />
                      {t(`whatsapp.questions.${key}`)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <label htmlFor="landing-whatsapp-custom" className="text-caption font-medium text-fg-secondary">
                {t('whatsapp.customLabel')}
              </label>
              <div className="mt-2 flex items-end gap-2">
                <Textarea
                  id="landing-whatsapp-custom"
                  value={customMessage}
                  onChange={(event) => setCustomMessage(event.target.value)}
                  placeholder={t('whatsapp.customPlaceholder')}
                  rows={2}
                  className="min-h-[4.5rem] resize-none"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleCustomSend();
                    }
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  className="size-11 shrink-0 bg-[#25D366] text-white hover:bg-[#20bd5a]"
                  aria-label={t('whatsapp.send')}
                  disabled={!customMessage.trim()}
                  onClick={handleCustomSend}
                >
                  <Send className="size-4" aria-hidden />
                </Button>
              </div>
              <p className="mt-2 text-[0.6875rem] leading-snug text-fg-secondary">{t('whatsapp.footnote')}</p>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className={cn(
          'pointer-events-auto landing-whatsapp-fab flex size-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-e3 transition-transform hover:scale-105 hover:bg-[#20bd5a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          open && 'rotate-0',
        )}
        aria-label={open ? t('whatsapp.close') : t('whatsapp.fabLabel')}
        aria-expanded={open}
        onClick={toggleOpen}
      >
        {open ? <X className="size-6" aria-hidden /> : <WhatsAppIcon className="size-7" />}
      </button>
    </div>
  );
};
